import Stack from '@mui/material/Stack'
import { SxProps } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { ReactElement } from 'react'

import { TreeBlock } from '../../libs/block'
import { useJourney } from '../../libs/JourneyProvider'
import { getJourneyRTL } from '../../libs/rtl'
import { StepFields } from '../Step/__generated__/StepFields'

import { InformationButton } from './InformationButton'
import { Logo } from './Logo'
import { PaginationBullets } from './PaginationBullets'
import { StepHeaderMenu } from './StepHeaderMenu'

interface StepHeaderProps {
  onHeaderClick?: () => void
  sx?: SxProps
  steps?: Array<TreeBlock<StepFields>> | null
  selectedStep?: TreeBlock<StepFields> | null
}

export function StepHeader({
  onHeaderClick,
  sx,
  steps,
  selectedStep
}: StepHeaderProps): ReactElement {
  const { journey, renderMode } = useJourney()
  const { rtl } = getJourneyRTL(journey)
  const { menuButtonIcon } = journey ?? {}
  const hasMenuButtonIcon = menuButtonIcon != null
  const isWebsite = journey?.website === true

  return (
    <Stack
      data-testid="JourneysStepHeader"
      sx={{
        position: { xs: 'absolute', lg: 'relative' },
        mt: { xs: 1, lg: 0 },
        height: { lg: 44 },
        zIndex: 1,
        top: 0,
        alignItems: 'flex-end',
        width: { xs: '100%', lg: 'auto' },
        py: 2,
        ...sx
      }}
      onClick={(e) => {
        if (onHeaderClick != null) {
          e.stopPropagation()
          onHeaderClick()
        }
      }}
    >
      {isWebsite ? (
        <Stack
          spacing={2}
          sx={{
            justifyContent: 'space-between',
            flexDirection: { lg: rtl ? 'row-reverse' : 'row' },
            alignItems: { xs: 'flex-start', lg: 'center' },
            width: '100%'
          }}
        >
          <Stack
            sx={{
              gap: 4,
              width: '100%',
              height: 52,
              flexDirection: rtl ? 'row-reverse' : 'row',
              alignItems: 'center'
            }}
          >
            <Logo />
            <Stack
              sx={{
                flex: '1 1 100%',
                minWidth: 0,
                alignItems: 'center'
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  zIndex: 1,
                  // Always dark mode on lg breakpoint
                  color: { xs: 'primary.main', lg: 'white' },
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '100%'
                }}
              >
                {journey?.displayTitle ?? journey?.seoTitle}
              </Typography>
            </Stack>
            {hasMenuButtonIcon ? <StepHeaderMenu /> : <InformationButton />}
          </Stack>
        </Stack>
      ) : (
        <>
          <PaginationBullets steps={steps} selectedStep={selectedStep} />
          <InformationButton
            sx={{ px: { xs: renderMode === 'default' ? 6 : 3, lg: 0 } }}
          />
        </>
      )}
    </Stack>
  )
}
